// src/components/common/RegistrationsList.js

import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { unregisterFromTournament } from '../../firebase/registrations';
import { getCurrentOrNextOccurrence, formatLocalDateTime } from '../../utils/recurringUtils';

export default function RegistrationsList({ tournament, isHost = false, instanceDate = null, onAddToPlayerList = null }) {
  const { currentUser } = useAuth();
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState(null);
  const [addingPlayers, setAddingPlayers] = useState(false);

  // Check if this is a doubles/scotch doubles tournament
  const isDoublesFormat = tournament.playerFormat === 'Doubles' || tournament.playerFormat === 'Scotch Doubles';

  // Determine instance date for recurring tournaments
  const targetInstanceDate = useMemo(() => {
    // For non-recurring tournaments, registrations are stored WITHOUT instanceDate
    // So we must return null to filter correctly, regardless of what's passed in
    if (!tournament.recurring?.enabled) {
      return null;
    }

    // For recurring tournaments: use explicitly provided instanceDate if available
    if (instanceDate) return instanceDate;

    // Otherwise, get the current or next occurrence
    // Use getCurrentOrNextOccurrence to match how RegisterButton calculates the date
    // Use formatLocalDateTime to avoid UTC conversion issues
    const currentOrNextDate = getCurrentOrNextOccurrence(tournament.recurring);
    return currentOrNextDate ? formatLocalDateTime(currentOrNextDate) : null;
  }, [tournament.recurring, instanceDate]);

  const registrations = tournament.registrations || { registeredUsers: [], maxCapacity: null };

  // Filter registrations for the current instance
  const registeredUsers = useMemo(() => {
    const allUsers = registrations.registeredUsers || [];

    if (!targetInstanceDate) {
      // Non-recurring tournament: show registrations without instanceDate
      return allUsers.filter(reg => !reg.instanceDate);
    }
    // Recurring tournament: show registrations for this specific instance
    return allUsers.filter(reg => reg.instanceDate === targetInstanceDate);
  }, [registrations.registeredUsers, targetInstanceDate]);

  const maxCapacity = registrations.maxCapacity;

  const handleRemoveRegistration = async (userId, userInstanceDate) => {
    if (!isHost) return;

    setRemoving(userId);
    setError(null);

    const { success, error: removeError } = await unregisterFromTournament(tournament.id, userId, userInstanceDate);

    if (!success) {
      setError(removeError?.message || 'Failed to remove registration');
    }

    setRemoving(null);
  };

  const handleAddAllToPlayerList = () => {
    if (!isHost || !onAddToPlayerList || registeredUsers.length === 0) return;

    setAddingPlayers(true);
    setError(null);

    // Get list of player names from registrations
    // For doubles, format as "Player & Partner"
    const playerNames = registeredUsers.map(reg => {
      if (isDoublesFormat && reg.partnerName) {
        return `${reg.displayName} & ${reg.partnerName}`;
      }
      return reg.displayName;
    });

    // Call the parent callback with the player names
    onAddToPlayerList(playerNames);

    setAddingPlayers(false);
  };

  const formatRegistrationDate = (registeredAt) => {
    if (!registeredAt) return 'Unknown';

    // Handle both Firestore Timestamp and JavaScript Date
    const date = registeredAt.toDate ? registeredAt.toDate() : new Date(registeredAt);

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  const formatInstanceDate = (instanceDateStr) => {
    if (!instanceDateStr) return null;

    const date = new Date(instanceDateStr);
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  if (registeredUsers.length === 0) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: 'var(--bg-tertiary)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '10px' }}>👥</div>
        <h3 style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)' }}>No Registrations Yet</h3>
        <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '14px' }}>
          {isHost ? 'Players will appear here when they register for your tournament.' : 'Be the first to register!'}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '8px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '15px 20px',
        backgroundColor: 'var(--accent-primary)',
        color: 'var(--bg-secondary)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
          {isDoublesFormat ? 'Registered Teams' : 'Registered Players'}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {isHost && onAddToPlayerList && registeredUsers.length > 0 && (
            <button
              onClick={handleAddAllToPlayerList}
              disabled={addingPlayers}
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--accent-primary)',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                cursor: addingPlayers ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                opacity: addingPlayers ? 0.7 : 1
              }}
              onMouseEnter={(e) => {
                if (!addingPlayers) {
                  e.target.style.backgroundColor = 'var(--bg-tertiary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!addingPlayers) {
                  e.target.style.backgroundColor = 'var(--bg-secondary)';
                }
              }}
            >
              {addingPlayers ? 'Adding...' : 'Add All to Players'}
            </button>
          )}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {registeredUsers.length}{maxCapacity ? `/${maxCapacity}` : ''}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div style={{
          padding: '12px 20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          fontSize: '14px',
          borderBottom: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Registrations list */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {registeredUsers.map((registration, index) => (
          <div
            key={registration.userId}
            style={{
              padding: '15px 20px',
              borderBottom: index < registeredUsers.length - 1 ? '1px solid var(--border-color)' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: index % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-tertiary)'
            }}
          >
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Player avatar/icon */}
              {registration.photoURL ? (
                <img
                  src={registration.photoURL}
                  alt={registration.displayName}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--bg-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {registration.displayName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}

              {/* Player info */}
              <div style={{ flex: 1 }}>
                <div style={{
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  fontSize: '16px',
                  marginBottom: '2px'
                }}>
                  {registration.displayName}
                  {registration.partnerName && (
                    <span style={{ color: 'var(--accent-primary)' }}> & {registration.partnerName}</span>
                  )}
                  {registration.userId === currentUser?.uid && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      color: 'var(--accent-primary)',
                      fontWeight: 'normal'
                    }}>
                      (You)
                    </span>
                  )}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: 'var(--text-secondary)'
                }}>
                  Registered {formatRegistrationDate(registration.registeredAt)}
                </div>
                {registration.instanceDate && (
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--accent-primary)',
                    marginTop: '2px',
                    fontWeight: 'bold'
                  }}>
                    For: {formatInstanceDate(registration.instanceDate)}
                  </div>
                )}
                {registration.ratings && (
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-muted)',
                    marginTop: '2px'
                  }}>
                    {registration.ratings.apa && `APA: ${registration.ratings.apa}`}
                    {registration.ratings.fargo && ` • Fargo: ${registration.ratings.fargo}`}
                  </div>
                )}
              </div>
            </div>

            {/* Remove button (only for hosts) */}
            {isHost && (
              <button
                onClick={() => handleRemoveRegistration(registration.userId, registration.instanceDate)}
                disabled={removing === registration.userId}
                style={{
                  padding: '6px 12px',
                  backgroundColor: removing === registration.userId ? 'var(--btn-secondary)' : 'var(--danger)',
                  color: 'var(--bg-secondary)',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: removing === registration.userId ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: removing === registration.userId ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (removing !== registration.userId) {
                    e.target.style.backgroundColor = '#c82333';
                  }
                }}
                onMouseLeave={(e) => {
                  if (removing !== registration.userId) {
                    e.target.style.backgroundColor = 'var(--danger)';
                  }
                }}
              >
                {removing === registration.userId ? 'Removing...' : 'Remove'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Capacity warning */}
      {maxCapacity && registeredUsers.length >= maxCapacity && (
        <div style={{
          padding: '12px 20px',
          backgroundColor: '#fff3cd',
          color: '#856404',
          fontSize: '14px',
          borderTop: '1px solid #ffeaa7',
          fontWeight: 'bold'
        }}>
          ⚠️ Tournament is at full capacity
        </div>
      )}
    </div>
  );
}
